import { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, FileUp } from "lucide-react";

export default function AdminCodes() {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight">Code Pool</h1>
      <p className="mt-2 text-zinc-500 text-sm">Manage the ISRC and UPC pool. Codes are auto-assigned to new submissions in order.</p>

      <Tabs defaultValue="isrc" className="mt-8">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="isrc" data-testid="tab-isrc">ISRC</TabsTrigger>
          <TabsTrigger value="upc" data-testid="tab-upc">UPC</TabsTrigger>
        </TabsList>
        <TabsContent value="isrc"><CodesTab kind="isrc" /></TabsContent>
        <TabsContent value="upc"><CodesTab kind="upc" /></TabsContent>
      </Tabs>
    </div>
  );
}

function CodesTab({ kind }) {
  const [codes, setCodes] = useState([]);
  const [input, setInput] = useState("");
  const [csvUploading, setCsvUploading] = useState(false);
  const csvRef = useRef(null);

  const load = () => api.get("/admin/codes", { params: { kind } }).then((r) => setCodes(r.data));
  useEffect(() => { load(); }, [kind]);

  const add = async () => {
    const list = input.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Enter one or more codes"); return; }
    try {
      const { data } = await api.post("/admin/codes", { kind, codes: list });
      toast.success(`Added ${data.added} codes`);
      setInput(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsvUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f);
      const { data } = await api.post("/admin/codes/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`CSV imported — added ${data.added}, skipped ${data.skipped} duplicates`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "CSV import failed");
    } finally {
      setCsvUploading(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/codes/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const available = codes.filter((c) => !c.used).length;
  const used = codes.length - available;

  return (
    <div className="mt-6 space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-transparent">
          <Ticket className="w-5 h-5 text-cyan-400 mb-4" />
          <div className="text-3xl font-bold" data-testid={`${kind}-available`}>{available}</div>
          <div className="text-xs uppercase tracking-wider text-zinc-400 mt-1">Available {kind.toUpperCase()}</div>
        </div>
        <div className="p-5 rounded-2xl border border-zinc-800 bg-zinc-950/60">
          <div className="text-3xl font-bold text-zinc-500">{used}</div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1">Assigned</div>
        </div>
      </div>

      <div className="p-5 rounded-2xl border border-zinc-800 bg-zinc-950/60">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-xs uppercase tracking-wider text-zinc-400">Add {kind.toUpperCase()} Codes</Label>
          <div className="flex items-center gap-2">
            <input ref={csvRef} type="file" accept=".csv,.txt,text/csv,text/plain" className="hidden" onChange={onCsv} data-testid={`${kind}-csv-input`} />
            <Button type="button" size="sm" variant="outline" onClick={() => csvRef.current?.click()}
              disabled={csvUploading} data-testid={`${kind}-csv-btn`}
              className="rounded-full border-zinc-700 hover:bg-zinc-900 text-cyan-400">
              <FileUp className="w-3.5 h-3.5 mr-1.5" /> {csvUploading ? "Uploading..." : "Import CSV"}
            </Button>
          </div>
        </div>
        <Textarea rows={3} value={input} onChange={(e) => setInput(e.target.value)}
          data-testid={`${kind}-input`}
          className="bg-zinc-900 border-zinc-800 font-mono"
          placeholder="Paste codes separated by comma, space or newline — or upload a CSV file above" />
        <Button onClick={add} data-testid={`${kind}-add`}
          className="mt-3 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Add to Pool
        </Button>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4">Code</th>
              <th className="p-4">Status</th>
              <th className="p-4">Added</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="p-4 font-mono">{c.code}</td>
                <td className="p-4">
                  <span className={`px-2 py-0.5 rounded-md text-xs border ${c.used ? "bg-zinc-800 text-zinc-400 border-zinc-700" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"}`}>
                    {c.used ? "Assigned" : "Available"}
                  </span>
                </td>
                <td className="p-4 text-zinc-500 text-sm">{new Date(c.added_at).toLocaleDateString()}</td>
                <td className="p-4 text-right">
                  {!c.used && (
                    <Button size="icon" variant="ghost" onClick={() => del(c.id)} className="h-8 w-8 text-zinc-400 hover:text-rose-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {codes.length === 0 && <tr><td colSpan="4" className="p-12 text-center text-zinc-500">No codes in pool yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
